<template>
  <ion-page>
    <WhiteHeader
      :back-button="true"
      route-back="/machine-reports"
      title="Entretien"
    />

    <ion-content class="ion-padding">
      <ion-row class="report-title">{{ humanDates }}</ion-row>

      <DateSelector
        v-if="week"
        :days="computedDays"
        :statuses="statuses"
        :selected-day="currentDay"
        @onDateClick="onDateClick($event)"
      />

      <ion-title v-if="!machines || !machines.length">
        Aucune mission cette semaine
      </ion-title>

      <div v-if="currentDay" class="report-container">
        <ion-segment
          v-if="machines && machines.length"
          :value="selectedMachine"
          @ionChange="selectedMachine = $event.detail.value"
          color="dark"
        >
          <ion-segment-button
            v-for="machine in machines"
            :key="machine.id"
            :value="machine.id"
          >
            <ion-label class="ion-text-capitalize">{{
              machine.name
            }}</ion-label>
          </ion-segment-button>
        </ion-segment>

        <div
          v-if="
            reports[selectedMachine][currentDay]
              .office_last_modified_by_full_name
          "
        >
          <ion-card color="light">
            <ion-card-content>
              <span>Dernière modification par&nbsp;:</span>

              <div>
                <strong>
                  {{
                    reports[selectedMachine][currentDay]
                      .office_last_modified_by_full_name
                  }}
                </strong>
                <span>
                  &nbsp;—&nbsp;
                  {{
                    formatDate(
                      reports[selectedMachine][currentDay]
                        .office_last_modified_at,
                    )
                  }}
                </span>
              </div>
            </ion-card-content>
          </ion-card>
        </div>

        <ion-grid
          v-for="(question, key) in questions[selectedMachine]"
          :key="'question' + key"
          class="question"
        >
          <ion-grid v-for="field in question.fields" :key="field.property">
            <!-- title -->
            <h5>{{ field.name }}</h5>

            <!-- TEXTAREA -->
            <div
              v-if="
                field.kind === questionKinds.TEXTAREA &&
                reports[selectedMachine] &&
                reports[selectedMachine][currentDay]
              "
            >
              <Textarea
                v-if="field.property"
                :title="field.name"
                v-model:value="
                  reports[selectedMachine][currentDay][field.property]
                "
                :disabled="statuses[currentDay] === 'full'"
              />
              <Textarea
                v-else-if="
                  reports[selectedMachine][currentDay].custom_questions
                "
                :title="field.name"
                v-model:value="
                  reports[selectedMachine][currentDay].custom_questions[
                    field.uniqid
                  ]
                "
                :disabled="statuses[currentDay] === 'full'"
              />
            </div>

            <!-- BOOLEAN -->
            <YesNoSelector
              v-if="
                field.kind === questionKinds.BOOLEAN &&
                reports[selectedMachine] &&
                reports[selectedMachine][currentDay]
              "
              :title="field.name"
              :value="getValueForBooleanField(field, true, selectedMachine)"
              @onValueChange="onBooleanChange($event, field, selectedMachine)"
              :disabled="statuses[currentDay] === 'full'"
              :remove-null-option="true"
            />

            <!-- DATE -->
            <Date
              v-if="field.kind === questionKinds.DATE"
              :title="field.name"
              :selected-day="currentDay"
              v-model:date="
                reports[selectedMachine][currentDay][field.property]
              "
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- DATERANGE -->
            <DateRange
              v-if="field.kind === questionKinds.DATERANGE"
              :title="field.name"
              :selected-day="currentDay"
              :dates="missionDays"
              v-model:start="
                reports[selectedMachine][currentDay][field.property][0]
              "
              v-model:end="
                reports[selectedMachine][currentDay][field.property][1]
              "
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- TIMESTAMP_NUMBER -->
            <TimestampNumber
              v-if="field.kind === questionKinds.TIMESTAMP_NUMBER"
              :title="field.name"
              :selected-day="currentDay"
              v-model:date="
                reports[selectedMachine][currentDay][
                  field.property + '_timestamp'
                ]
              "
              v-model:value="
                reports[selectedMachine][currentDay][field.property]
              "
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- NUMBER -->
            <Number
              v-if="field.kind === questionKinds.NUMBER && field.uniqid"
              :title="field.name"
              v-model:value="
                reports[selectedMachine][currentDay]['custom_questions'][
                  field.uniqid
                ]
              "
              :disabled="statuses[currentDay] === 'full'"
            />
            <Number
              v-else-if="field.kind === questionKinds.NUMBER"
              :title="field.name"
              :placeholders="field.placeholders"
              v-model:value="
                reports[selectedMachine][currentDay][field.property]
              "
              :disabled="statuses[currentDay] === 'full'"
            />

            <!-- When editing is disabled simply allow to consult existing files without adding new -->
            <AttachmentsList
              v-if="
                field.kind === questionKinds.FILE &&
                statuses[currentDay] === 'full' &&
                reports[selectedMachine][currentDay]
              "
              :attachments="
                field.uniqid
                  ? reports[selectedMachine][currentDay]['custom_questions'][
                      field.uniqid
                    ]
                  : reports[selectedMachine][currentDay][field.property]
              "
            />

            <!-- FILE -->
            <!-- if uniqid it's a custom question, else it's a "static" file type (e.g. "photo") -->
            <Attachments
              v-else-if="field.kind === questionKinds.FILE && field.uniqid"
              v-model:attachments="
                reports[selectedMachine][currentDay]['custom_questions'][
                  field.uniqid
                ]
              "
              v-model:waiting-files="waitingFiles"
              v-model:attachments-to-delete="attachmentsToDelete"
              v-model:attachments-to-update="attachmentsToUpdate"
              type-vue="create"
              :files-too-big="false"
              :kind="field.uniqid"
              :title="field.name"
              :status="statuses[currentDay] === 'full' ? 'send' : 'create'"
            />
            <Attachments
              v-else-if="field.kind === questionKinds.FILE"
              v-model:attachments="
                reports[selectedMachine][currentDay][field.property]
              "
              v-model:waiting-files="waitingFiles"
              v-model:attachments-to-delete="attachmentsToDelete"
              v-model:attachments-to-update="attachmentsToUpdate"
              type-vue="create"
              :files-too-big="false"
              :kind="field.property"
              :title="field.name"
              :status="statuses[currentDay] === 'full' ? 'send' : 'create'"
            />
          </ion-grid>
        </ion-grid>
      </div>
    </ion-content>

    <div
      v-if="currentDay && machines && machines.length"
      class="bottom-buttons"
    >
      <ion-button
        expand="block"
        fill="outline"
        class="save"
        @click="sendAs('draft')"
        :disabled="sending"
      >
        Enregistrer le brouillon
      </ion-button>

      <ion-button
        expand="block"
        class="next-step"
        @click="sendAs('sent')"
        :disabled="sending"
      >
        Valider et envoyer
      </ion-button>
    </div>
  </ion-page>
</template>

<script>
import { getParams } from '@/services/users';
import WhiteHeader from '@/components/driver/header/WhiteHeader';
import { chevronUpOutline, chevronDownOutline } from 'ionicons/icons';
import {
  IonPage,
  IonContent,
  IonButton,
  IonGrid,
  IonRow,
  IonLabel,
  IonTitle,
  IonSegment,
  IonSegmentButton,
  IonCard,
  IonCardContent,
} from '@ionic/vue';
import axios from 'axios';
import dayjs from 'dayjs';
import { makeWeek, formatDate } from '@/services/utils/reportUtils';
import questionKinds from '@/services/constants/questionKinds';
import DateSelector from '@/components/driver/rentalCoupon/report-components/DateSelector.vue';
import DateRange from '@/components/driver/rentalCoupon/report-components/DateRange.vue';
import Date from '@/components/driver/rentalCoupon/report-components/Date.vue';
import Textarea from '@/components/driver/rentalCoupon/report-components/Textarea.vue';
import TimestampNumber from '@/components/driver/rentalCoupon/report-components/TimestampNumber.vue';
import Number from '@/components/driver/rentalCoupon/report-components/Number.vue';
import Attachments from '@/components/driver/rentalCoupon/components/Attachments.vue';
import YesNoSelector from '@/components/driver/rentalCoupon/report-components/YesNoSelector.vue';
import { mixinReportCommon } from '@/components/driver/rentalCoupon/mixins/ReportCommon';
import AttachmentsList from '@/components/driver/detailMission/sub-components/AttachmentsList.vue';

const DAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export default {
  name: 'TimeReportUpdate',

  components: {
    IonPage,
    IonContent,
    IonButton,
    IonGrid,
    IonRow,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonTitle,
    IonCard,
    IonCardContent,

    // custom components
    WhiteHeader,
    DateSelector,

    // question components
    DateRange,
    Date,
    Textarea,
    TimestampNumber,
    Number,
    Attachments,
    AttachmentsList,
    YesNoSelector,
  },

  mixins: [mixinReportCommon],

  setup() {
    return {
      chevronUpOutline,
      chevronDownOutline,
      questionKinds,
    };
  },

  data() {
    return {
      week: null,
      days: DAYS,
      dates: [],
      computedDays: {},
      questions: {},
      reports: {},
      machines: {},
      selectedMachine: null,
      currentDay: null,
      sending: false,

      // files
      waitingFiles: [],
      attachmentsToDelete: [],
      attachmentsToUpdate: {},
    };
  },

  mounted() {
    const start = dayjs(this.$route.params.date).startOf('week');
    DAYS.forEach((day, index) => {
      this.dates.push(start.add(index, 'days').format('YYYY-MM-DD'));
    });
    this.week = makeWeek(start);

    const formatted = {};
    Object.values(this.dates).forEach(day => {
      formatted[this.formatDate(day)] = day;
    });
    this.computedDays = formatted;

    this.fetchDetail();
  },

  methods: {
    async fetchDetail() {
      axios
        .get(
          `/driver/machine-report/${this.$route.params.date}`,
          await getParams(),
        )
        .then(response => {
          this.reports = response.data.reports;
          this.questions = response.data.questions;
          this.machines = response.data.machines;

          // Set currentDay to the clicked day from params only if there are machines
          if (this.machines && this.machines.length) {
            this.currentDay = this.$route.params.date;
          }
        })
        .finally(() => {
          this.sending = false;
        });
    },

    formatDate,

    /**
     * Override mixin method because we have one more depth for machines
     */
    onDateClick(newDate) {
      // if no machine selected, do nothing
      if (!this.selectedMachine) return;

      this.currentDay = newDate;

      if (!this.reports[this.selectedMachine]) {
        this.reports[this.selectedMachine] = {};
      }

      const currentReport = this.reports[this.selectedMachine][this.currentDay];

      let reportForDay = currentReport
        ? JSON.parse(JSON.stringify(currentReport))
        : {};

      if (!reportForDay.custom_questions) {
        reportForDay.custom_questions = {};
      }

      reportForDay = this.onDateClickCommon(reportForDay, this.selectedMachine);
      this.reports[this.selectedMachine][this.currentDay] = reportForDay;
    },

    // override reportCommon getAllDataToSend because we have one more depth for machines
    getAllDataToSend() {
      const formData = this.getWaitingFilesFormData();
      const dataToSend = this.getMissionRelatedData();

      const reportsData = {};

      // foreach machine reports, get data to send
      Object.entries(this.reports).forEach(([machineId, reports]) => {
        const data = this.getDataToSend(
          'machine_reports',
          reports,
          this.selectedMachine,
        );

        reportsData[machineId] = data;
      });

      dataToSend['reports'] = reportsData;

      dataToSend.attachmentsToUpdate = this.attachmentsToUpdate;
      dataToSend.attachmentsToDelete = this.attachmentsToDelete;
      formData.append('report_data', JSON.stringify(dataToSend));

      return formData;
    },

    async sendAs(status) {
      const dataToSend = this.getAllDataToSend();

      const reportData = JSON.parse(dataToSend.get('report_data'));

      const newReports = {};
      Object.entries(reportData.reports).forEach(([machineId, reports]) => {
        const machineReports = {};

        Object.entries(reports).forEach(([day, report]) => {
          const status = this.getCompletionStatuses(report, machineId);

          if (status === 'empty') {
            return;
          }

          machineReports[day] = report;
        });

        // don't add filtered reports if no report left
        if (!Object.keys(machineReports).length) {
          return;
        }

        newReports[machineId] = machineReports;
      });

      // if no report to send, do nothing
      if (!Object.keys(newReports).length) {
        this.sendToast('Aucun rapport à envoyer.', 'danger');
        return;
      }

      // ask confirmation if we want to send reports
      if (status === 'sent') {
        // Extract all days across all machines
        const daysToSend = [];
        Object.values(newReports).forEach(machineReports => {
          Object.values(machineReports).forEach(report => {
            const day = report.date;

            if (!daysToSend.includes(day)) {
              daysToSend.push(day);
            }
          });
        });

        const selectedDays = await this.confirm(daysToSend, 'machine-reports');

        // If user cancelled
        if (selectedDays === null) return;

        // note that if user didn't select any days, we still send them
        // but only with 'draft' status

        // Set status for each report based on selection
        const updatedReports = {};
        Object.entries(newReports).forEach(([machineId, machineReports]) => {
          const updated = {};

          Object.entries(machineReports).forEach(([key, report]) => {
            updated[key] = {
              ...report,
              status: selectedDays.includes(report.date) ? 'sent' : 'draft',
            };
          });

          updatedReports[machineId] = updated;
        });

        reportData.reports = updatedReports;
      } else {
        reportData.reports = newReports;
      }

      dataToSend.set('report_data', JSON.stringify(reportData));

      this.sending = true;
      try {
        await axios.post(
          `/driver/machine-report`,
          dataToSend,
          await getParams(),
        );

        this.sendToast(
          status === 'draft'
            ? 'OK, données enregistrées en brouillon.'
            : 'OK, données envoyées.',
        );
      } catch {
        this.sendToast("Erreur lors de l'envoi.", 'danger');
      }

      this.fetchDetail();
    },
  },

  computed: {
    humanDates() {
      if (!this.week) return '';

      return `Du ${this.week.formattedStart} au ${this.week.formattedEnd}`;
    },

    statuses() {
      if (!this.reports || !Object.keys(this.reports)) return {};

      const computedStatuses = {};

      Object.values(this.reports).forEach(entries => {
        Object.entries(entries).forEach(([day, report]) => {
          if (!computedStatuses[day]) {
            computedStatuses[day] = 'empty';
          }

          // if report is sent, set status to full
          if (report.status === 'sent') {
            computedStatuses[day] = 'full';
          } else if (report.status === 'draft') {
            // if report is draft, set status to partial
            computedStatuses[day] = 'partial';
          }
        });
      });

      return computedStatuses;
    },
  },

  watch: {
    machines(newValue) {
      // if machine already selected, do not change it
      if (this.selectedMachine) {
        this.onDateClick(this.currentDay || this.dates[0]);

        return;
      }

      this.selectedMachine = newValue.length ? newValue[0].id : null;
    },

    selectedMachine() {
      // if current day and no hours details, initialize it
      this.onDateClick(this.currentDay || this.dates[0]);
    },
  },
};
</script>

<style scoped lang="scss">
ion-content {
  --padding-start: 0;
  --padding-end: 0;
  --padding-bottom: 0;
  --padding-top: 0;
  --background: #fffcfa;
}

.report-container {
  padding: 0 16px;
  padding-bottom: 100px; /* Add padding to prevent content from being hidden behind bottom bar */
}

h3 {
  margin-top: 32px;
  font-size: 16px;
  font-weight: bold;
}

.question {
  padding: 0 8px;
}

.h-100 {
  height: 100%;
}

ion-title {
  text-align: center;
  margin-top: 50px;
}

ion-segment-button {
  &.md::part(indicator-background) {
    height: 3px;
  }

  &::part(indicator-background) {
    background: #fadd90;
  }
}

.segment-button-checked.md::part(native) {
  color: #745b00;
  font-weight: 600;
}
</style>
